import { useTranslation } from 'react-i18next';
import type { LeadProposalGenerationType } from '@econtractor/types';
import { formatPrice } from 'utils/helpers/functions/formatPrice';
import {
    getBaseCurrencyCode,
    getCurrencyCode,
    withBaseCurrency,
    withLocalCurrency,
} from 'utils/helpers/currency/currencyHelpers';
import { safeFloatParse } from 'utils/helpers/safeParsing';

interface LeadApprovedProposalProps {
    generation?: LeadProposalGenerationType | null;
    loading?: boolean;
    /** Compact badge for the commercial proposal card */
    compact?: boolean;
    hasLeadProposal?: boolean;
    onOpenProposal?: (() => void) | null;
}

const formatGenerationAmount = (amount: number, isUsd: boolean) => {
    const formatted = formatPrice(safeFloatParse(amount || 0));
    return isUsd ? withBaseCurrency(formatted) : withLocalCurrency(formatted);
};

const LeadApprovedProposal = ({
    generation = null,
    loading = false,
    compact = false,
    hasLeadProposal = false,
    onOpenProposal = null,
}: LeadApprovedProposalProps) => {
    const { t } = useTranslation(['Proposal']);
    const isAccepted = Boolean(generation);
    const isUsd = Boolean(generation?.proposalUsd);
    const withVat = Boolean(generation?.proposalVat);
    const amountLabel = generation
        ? formatGenerationAmount(generation.proposalAmount, isUsd)
        : '—';
    const vatLabel = withVat ? t('lead.approved.withVat') : t('lead.approved.withoutVat');
    const currencyLabel = isUsd ? getBaseCurrencyCode() : getCurrencyCode();

    if (compact) {
        if (loading) return null;
        return (
            <div className="lead-proposal-accepted-compact">
                {isAccepted ? (
                    <>
                        <span className="lead-proposal-accepted-compact__amount">{amountLabel}</span>
                        <span className="lead-proposal-accepted-compact__meta">
                            {vatLabel} · {currencyLabel}
                        </span>
                    </>
                ) : (
                    <span className="lead-proposal-accepted-compact__empty">
                        <i className="bi bi-dash-circle" aria-hidden="true"></i>
                        {t('lead.approved.statusEmpty')}
                    </span>
                )}
            </div>
        );
    }

    const tone = isAccepted ? 'covered' : 'empty';

    return (
        <div className={`object-analytics-payments object-analytics-payments--${tone} lead-approved-proposal`}>
            <div className="object-analytics-payments__glow" aria-hidden="true" />
            <div className="object-analytics-payments__head">
                <span className="object-analytics-payments__icon" aria-hidden="true">
                    <i className={`bi ${isAccepted ? 'bi-file-earmark-check' : 'bi-file-earmark'}`}></i>
                </span>
                <div className="object-analytics-payments__titles">
                    <div className="object-analytics-payments__title-row">
                        <div className="object-analytics-payments__title-stack">
                            <span className="object-analytics-payments__label">
                                {t('lead.approved.blockTitle')}
                            </span>
                            <span className="object-analytics-payments__hint">
                                {isAccepted
                                    ? generation?.proposalName || t('lead.approved.hintAccepted')
                                    : t('lead.approved.hintEmpty')}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="object-analytics-payments__chips">
                {isAccepted ? (
                    <>
                        <div className="object-analytics-payments__chip">
                            <span className="object-analytics-payments__chip-cur">{currencyLabel}</span>
                            <span className="object-analytics-payments__chip-val">{amountLabel}</span>
                        </div>
                        <div className="object-analytics-payments__chip">
                            <span className="object-analytics-payments__chip-cur">
                                {t('edit.general.vatLabel')}
                            </span>
                            <span className="object-analytics-payments__chip-val lead-approved-proposal__vat">
                                {vatLabel}
                            </span>
                        </div>
                    </>
                ) : (
                    <div className="object-analytics-payments__chip object-analytics-payments__chip--empty">
                        <span className="object-analytics-payments__chip-val">
                            {loading ? t('lead.approved.loading') : '—'}
                        </span>
                    </div>
                )}
            </div>

            {onOpenProposal ? (
                <div className="lead-approved-proposal__action">
                    <button
                        type="button"
                        className="lead-approved-proposal__action-btn"
                        onClick={() => onOpenProposal()}
                    >
                        <i
                            className={`bi ${hasLeadProposal ? 'bi-pencil-square' : 'bi-file-earmark-plus'}`}
                            aria-hidden="true"
                        ></i>
                        {hasLeadProposal
                            ? t('lead.approved.actionEdit')
                            : t('lead.approved.actionCreate')}
                    </button>
                </div>
            ) : null}
        </div>
    );
};

export default LeadApprovedProposal;
